import { useState, useRef, useEffect } from "react";
import { X, Upload, Play, Pause, Scissors, Type, Crop, Camera, Music, Layers,
  Loader2, Trash2, Plus, Bold, Volume2, VolumeX, Copy as CopyIcon, Gauge, SplitSquareHorizontal,
  ChevronLeft, ChevronRight, Download, Film, GripVertical, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { uploadReferenceVideo, uploadAsset } from "@/lib/uploadVideo";

const MAX_TOTAL = 600; // 10 minutes
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const fmt = (s) => { s = Math.max(0, s || 0); const m = Math.floor(s / 60); const r = (s % 60); return `${m}:${r.toFixed(1).padStart(4, "0")}`; };
const uid = () => Math.random().toString(36).slice(2, 9);
const eff = (c) => (c.trimEnd - c.trimStart) / c.speed;

const TOOLS = [
  { id: "trim", icon: Scissors, label: "Trim" },
  { id: "split", icon: SplitSquareHorizontal, label: "Split" },
  { id: "copy", icon: CopyIcon, label: "Copy" },
  { id: "speed", icon: Gauge, label: "Speed" },
  { id: "text", icon: Type, label: "Text" },
  { id: "sound", icon: Music, label: "Sound" },
  { id: "capture", icon: Camera, label: "Capture" },
  { id: "download", icon: Download, label: "Download" },
  { id: "crop", icon: Crop, label: "Crop" },
  { id: "overlay", icon: Layers, label: "Overlay" },
];

function Draggable({ el, stageRef, selected, onSelect, onChange, children }) {
  const start = useRef(null);
  const down = (e, mode) => {
    e.stopPropagation(); onSelect();
    const rect = stageRef.current.getBoundingClientRect();
    start.current = { mode, px: e.clientX, py: e.clientY, rect, el: { ...el } };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const move = (e) => {
    if (!start.current) return;
    const { mode, px, py, rect, el: s } = start.current;
    const dx = (e.clientX - px) / rect.width, dy = (e.clientY - py) / rect.height;
    if (mode === "move") onChange({ x: clamp(s.x + dx, 0, 0.98), y: clamp(s.y + dy, 0, 0.98) });
    else if (el.type === "text") onChange({ h: clamp((s.h || 0.1) + dy, 0.03, 0.6) });
    else onChange({ w: clamp((s.w || 0.3) + dx, 0.05, 1), h: clamp((s.h || 0.3) + dy, 0.05, 1) });
  };
  const up = () => { start.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  const style = el.type === "text"
    ? { left: `${el.x * 100}%`, top: `${el.y * 100}%`, fontSize: `calc(${el.h} * var(--stageH))`, color: el.color, fontWeight: el.bold ? 800 : 500 }
    : { left: `${el.x * 100}%`, top: `${el.y * 100}%`, width: `${el.w * 100}%`, height: `${el.h * 100}%` };
  return (
    <div data-testid={`stitch-el-${el.id}`} onPointerDown={(e) => down(e, "move")}
      className={`absolute select-none touch-none cursor-move ${selected ? "ring-2 ring-gold" : ""}`} style={style}>
      {children}
      {selected && <div onPointerDown={(e) => down(e, "resize")} data-testid={`stitch-resize-${el.id}`}
        className="absolute -right-2 -bottom-2 w-5 h-5 rounded-full bg-gold border-2 border-ink-950 cursor-se-resize touch-none" />}
    </div>
  );
}

function CropBox({ crop, setCrop, stageRef }) {
  const start = useRef(null);
  const down = (e, mode) => {
    e.stopPropagation();
    const rect = stageRef.current.getBoundingClientRect();
    start.current = { mode, px: e.clientX, py: e.clientY, rect, c: { ...crop } };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const move = (e) => {
    if (!start.current) return;
    const { mode, px, py, rect, c } = start.current;
    const dx = (e.clientX - px) / rect.width, dy = (e.clientY - py) / rect.height;
    if (mode === "move") setCrop({ ...c, x: clamp(c.x + dx, 0, 1 - c.w), y: clamp(c.y + dy, 0, 1 - c.h) });
    else setCrop({ ...c, w: clamp(c.w + dx, 0.1, 1 - c.x), h: clamp(c.h + dy, 0.1, 1 - c.y) });
  };
  const up = () => { start.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  return (
    <div data-testid="stitch-crop-box" onPointerDown={(e) => down(e, "move")}
      className="absolute border-2 border-gold cursor-move touch-none"
      style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%`, boxShadow: "0 0 0 9999px rgba(0,0,0,.5)" }}>
      <div onPointerDown={(e) => down(e, "resize")} className="absolute -right-2 -bottom-2 w-5 h-5 rounded-full bg-gold border-2 border-ink-950 cursor-se-resize touch-none" />
    </div>
  );
}

export default function StitchEditor({ open, onOpenChange, onExported }) {
  const [clips, setClips] = useState([]);
  const [picking, setPicking] = useState(false); // false | 'first' | 'add'
  const [myVideos, setMyVideos] = useState([]);
  const [uploadPct, setUploadPct] = useState(null);

  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);              // global playhead
  const ciRef = useRef(0);

  const [tool, setTool] = useState("trim");
  const [selClip, setSelClip] = useState(null);
  const [crop, setCrop] = useState(null);
  const [texts, setTexts] = useState([]);
  const [overlays, setOverlays] = useState([]);
  const [audio, setAudio] = useState(null);
  const [sel, setSel] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ---- undo / redo (snapshots of editor content; rapid drags/slider moves coalesce into one step) ----
  const [hist, setHist] = useState({ past: [], future: [] });
  const lastSnap = useRef(null);
  const burst = useRef(null);
  const applying = useRef(false);
  useEffect(() => {
    const snap = { clips, crop, texts, overlays, audio };
    if (applying.current || !lastSnap.current) { applying.current = false; lastSnap.current = snap; return; }
    if (!burst.current) { const prev = lastSnap.current; setHist((h) => ({ past: [...h.past, prev].slice(-100), future: [] })); }
    clearTimeout(burst.current);
    burst.current = setTimeout(() => { burst.current = null; }, 400);
    lastSnap.current = snap;
  }, [clips, crop, texts, overlays, audio]);
  const applySnap = (s) => { applying.current = true; setClips(s.clips); setCrop(s.crop); setTexts(s.texts); setOverlays(s.overlays); setAudio(s.audio); };
  const undo = () => {
    clearTimeout(burst.current); burst.current = null;
    if (!hist.past.length) return;
    const prev = hist.past[hist.past.length - 1];
    setHist({ past: hist.past.slice(0, -1), future: [lastSnap.current, ...hist.future] });
    applySnap(prev);
  };
  const redo = () => {
    clearTimeout(burst.current); burst.current = null;
    if (!hist.future.length) return;
    const next = hist.future[0];
    setHist({ past: [...hist.past, lastSnap.current], future: hist.future.slice(1) });
    applySnap(next);
  };
  const undoRef = useRef(undo); undoRef.current = undo;
  const redoRef = useRef(redo); redoRef.current = redo;
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redoRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const starts = []; let acc = 0; for (const c of clips) { starts.push(acc); acc += eff(c); }
  const total = acc;
  const ciFor = (gt) => { for (let i = clips.length - 1; i >= 0; i--) if (gt >= starts[i] - 1e-3) return i; return 0; };

  useEffect(() => { if (open) api.get("/videos").then(({ data }) => setMyVideos((data || []).filter((v) => v.status === "completed" && v.video_url))).catch(() => {}); }, [open]);

  const reset = () => { setClips([]); setTexts([]); setOverlays([]); setCrop(null); setAudio(null); setSel(null); setSelClip(null); setT(0); ciRef.current = 0; setTool("trim"); setPlaying(false); setPicking(false);
    clearTimeout(burst.current); burst.current = null; lastSnap.current = null; setHist({ past: [], future: [] }); };

  const addClipFromUrl = (url) => new Promise((res) => {
    const v = document.createElement("video");
    v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = url;
    let done = false;
    const finish = (meta) => { if (done) return; done = true; try { v.removeAttribute("src"); v.load(); } catch { /* noop */ }
      res({ id: uid(), url, thumb: meta.thumb || null, w: meta.w || 0, h: meta.h || 0, duration: meta.duration || 0, trimStart: 0, trimEnd: meta.duration || 0, speed: 1.0 }); };
    v.onloadedmetadata = () => {
      const w = v.videoWidth, h = v.videoHeight, duration = v.duration || 0;
      const grab = () => {
        let thumb = null;
        try {
          const scale = 200 / Math.max(1, w);
          const cv = document.createElement("canvas");
          cv.width = Math.max(1, Math.round(w * scale)); cv.height = Math.max(1, Math.round(h * scale));
          cv.getContext("2d").drawImage(v, 0, 0, cv.width, cv.height);
          thumb = cv.toDataURL("image/jpeg", 0.6);
        } catch { thumb = null; }
        finish({ w, h, duration, thumb });
      };
      v.onseeked = grab;
      setTimeout(grab, 1200); // fallback if seek never fires (mobile codecs)
      try { v.currentTime = Math.min(0.1, duration / 2 || 0.1); } catch { grab(); }
    };
    v.onerror = () => finish({ w: 0, h: 0, duration: 0, thumb: null });
  });

  const pickVideo = async (url) => {
    if (total >= MAX_TOTAL) { toast.error("Project is at the 10-minute limit."); return; }
    const c = await addClipFromUrl(url);
    setClips((a) => [...a, c]); setSelClip(c.id); setPicking(false);
  };

  const uploadVideo = () => {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = "video/*"; inp.multiple = true;
    inp.onchange = async () => {
      const files = Array.from(inp.files || []); if (!files.length) return;
      setPicking(false);
      let running = total, added = 0, failed = 0, limitHit = false;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        try {
          setUploadPct(0);
          toast.loading(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : "Uploading…", { id: "stitch-up" });
          const { url } = await uploadReferenceVideo(f, setUploadPct);
          const c = await addClipFromUrl(url);
          if (running + eff(c) > MAX_TOTAL) { limitHit = true; break; }
          running += eff(c); added++;
          setClips((a) => [...a, c]); setSelClip(c.id);
        } catch (e) {
          failed++;
          toast.error(`${formatApiError(e.response?.data?.detail)}${f.name ? ` (${f.name})` : ""}`);
        }
      }
      setUploadPct(null);
      toast.dismiss("stitch-up");
      if (added) toast.success(added > 1 ? `${added} clips added` : "Clip added");
      if (limitHit) toast.error("10-minute limit reached — remaining clips skipped.");
      else if (failed && !added) toast.error(failed > 1 ? "None of the clips could be uploaded." : "The clip couldn't be uploaded.");
      else if (failed) toast.error(`${failed} of ${files.length} clips failed to upload.`);
    };
    inp.click();
  };

  // ---- preview playback across clips ----
  useEffect(() => {
    if (!clips.length) return;
    const ci = ciFor(t); ciRef.current = ci;
    const v = videoRef.current; if (!v) return;
    const c = clips[ci];
    const localSrc = c.trimStart + (t - starts[ci]) * c.speed;
    if (!v.src.includes(encodeURI(c.url)) && v.src !== c.url) { v.src = c.url; }
    v.playbackRate = c.speed;
    const setCT = () => { try { v.currentTime = clamp(localSrc, 0, c.duration || localSrc); } catch { /* seeking */ } };
    if (v.readyState >= 1) setCT(); else v.onloadedmetadata = setCT;
    if (stageRef.current) stageRef.current.style.setProperty("--stageH", `${stageRef.current.clientHeight}px`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciRef.current, clips.length]);

  const onTimeUpdate = () => {
    const v = videoRef.current; if (!v || !clips.length) return;
    const ci = ciRef.current; const c = clips[ci]; if (!c) return;
    if (v.currentTime >= c.trimEnd - 0.03) {
      if (ci < clips.length - 1) { ciRef.current = ci + 1; const nc = clips[ci + 1]; v.src = nc.url; v.playbackRate = nc.speed; v.onloadeddata = () => { v.currentTime = nc.trimStart; if (playing) v.play(); }; setT(starts[ci + 1]); return; }
      v.pause(); setPlaying(false); setT(0); ciRef.current = 0; return;
    }
    setT(starts[ci] + (v.currentTime - c.trimStart) / c.speed);
  };

  const togglePlay = () => {
    const v = videoRef.current; if (!v || !clips.length) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const seek = (gt) => { setT(gt); const ci = ciFor(gt); ciRef.current = ci; const c = clips[ci]; const v = videoRef.current; if (v) { if (!v.src.includes(c.url)) v.src = c.url; v.playbackRate = c.speed; try { v.currentTime = c.trimStart + (gt - starts[ci]) * c.speed; } catch { /* seek */ } } };

  const rel = t; // global time
  const cur = clips.find((c) => c.id === selClip) || clips[ciRef.current];
  const updClip = (id, patch) => setClips((a) => a.map((c) => c.id === id ? { ...c, ...patch } : c));

  // ---- clip actions ----
  const splitAt = () => {
    const ci = ciFor(t); const c = clips[ci]; if (!c) return;
    const localSrc = c.trimStart + (t - starts[ci]) * c.speed;
    if (localSrc <= c.trimStart + 0.1 || localSrc >= c.trimEnd - 0.1) { toast.error("Move the playhead into the clip to split."); return; }
    const a = { ...c, id: uid(), trimEnd: localSrc };
    const b = { ...c, id: uid(), trimStart: localSrc };
    setClips((arr) => arr.flatMap((x) => x.id === c.id ? [a, b] : [x]));
    setSelClip(a.id); toast.success("Clip split");
  };
  const copyClip = () => {
    const c = clips.find((x) => x.id === selClip); if (!c) { toast.error("Select a clip first"); return; }
    if (total + eff(c) > MAX_TOTAL) { toast.error("Copy would exceed the 10-minute limit."); return; }
    const dup = { ...c, id: uid() };
    setClips((arr) => { const i = arr.findIndex((x) => x.id === c.id); const n = [...arr]; n.splice(i + 1, 0, dup); return n; });
    setSelClip(dup.id); toast.success("Clip copied");
  };
  const delClip = (id) => { setClips((a) => a.filter((c) => c.id !== id)); if (selClip === id) setSelClip(null); };
  const moveClip = (id, dir) => setClips((a) => { const i = a.findIndex((c) => c.id === id); const j = i + dir; if (j < 0 || j >= a.length) return a; const n = [...a]; [n[i], n[j]] = [n[j], n[i]]; return n; });

  // drag-to-reorder clips in the filmstrip (pointer-based, works on touch + mouse)
  const [dragId, setDragId] = useState(null);
  const reorderTo = (targetId, fromId) => setClips((a) => {
    if (!fromId || fromId === targetId) return a;
    const fi = a.findIndex((c) => c.id === fromId), ti = a.findIndex((c) => c.id === targetId);
    if (fi < 0 || ti < 0) return a;
    const n = [...a]; const [m] = n.splice(fi, 1); n.splice(ti, 0, m); return n;
  });
  const onGripDown = (id) => (e) => {
    e.stopPropagation(); e.preventDefault(); setDragId(id); setSelClip(id);
    const move = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const tile = el && el.closest && el.closest("[data-clip-id]");
      const overId = tile && tile.getAttribute("data-clip-id");
      if (overId && overId !== id) reorderTo(overId, id);
    };
    const up = () => { setDragId(null); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const addText = () => { const id = uid(); setTexts((x) => [...x, { id, type: "text", text: "Your text", color: "#ffffff", bold: true, x: 0.2, y: 0.4, h: 0.09, start: +t.toFixed(2), end: +Math.min(t + 2, total).toFixed(2) }]); setSel(id); setTool("text"); };

  const pickOverlay = (kind) => {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = kind === "image" ? "image/*" : "video/*";
    inp.onchange = async () => { const f = inp.files?.[0]; if (!f) return;
      try { setUploadPct(0); let url;
        if (kind === "image") { const b64 = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(f); }); const { data } = await api.post("/uploads/image", { image_base64: b64, origin_url: window.location.origin }); url = data.url; }
        else { const { url: u } = await uploadReferenceVideo(f, setUploadPct); url = u; }
        const id = uid(); setOverlays((o) => [...o, { id, type: kind, url, x: 0.3, y: 0.3, w: 0.35, h: 0.35, start: 0, end: total }]); setSel(id);
      } catch { toast.error("Upload failed"); } finally { setUploadPct(null); } };
    inp.click();
  };
  const pickAudio = () => {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = "audio/*";
    inp.onchange = async () => { const f = inp.files?.[0]; if (!f) return;
      try { setUploadPct(0); const { url } = await uploadAsset(f, setUploadPct); setAudio({ url, name: f.name, volume: 1, muteOriginal: false }); toast.success("Audio added"); }
      catch { toast.error("Audio upload failed"); } finally { setUploadPct(null); } };
    inp.click();
  };

  const capture = async () => {
    const ci = ciFor(t); const c = clips[ci]; if (!c) return;
    const localSrc = c.trimStart + (t - starts[ci]) * c.speed;
    try { toast.loading("Capturing frame…", { id: "cap" }); await api.post("/stitch/capture", { source_url: c.url, time: localSrc }); toast.success("Frame saved to Creations", { id: "cap" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Capture failed", { id: "cap" }); }
  };

  const downloadClip = async () => {
    const c = clips.find((x) => x.id === selClip) || clips[ciRef.current]; if (!c) { toast.error("Select a clip first"); return; }
    if (downloading) return;
    setDownloading(true);
    toast.loading("Preparing video…", { id: "dl" });
    const spec = {
      clips: [{ url: c.url, trim: { start: +c.trimStart.toFixed(3), end: +c.trimEnd.toFixed(3) }, speed: +c.speed.toFixed(3) }],
      crop: null, mute_original: false, audio: null, overlays: [], origin_url: window.location.origin,
    };
    try {
      const { data } = await api.post("/stitch/export", spec);
      const url = data.video_url;
      toast.success("Saved to Creations 🎬 — downloading…", { id: "dl" });
      try {
        const resp = await fetch(url); const blob = await resp.blob();
        const a = document.createElement("a"); const obj = URL.createObjectURL(blob);
        a.href = obj; a.download = `stitch-clip-${Date.now()}.mp4`; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(obj), 5000);
      } catch { window.open(url, "_blank"); }
      onExported && onExported();
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed", { id: "dl" }); }
    finally { setDownloading(false); }
  };

  const doExport = async () => {
    if (!clips.length) return;
    if (total <= 0) { toast.error("Clips are still loading — give it a moment."); return; }
    if (total > MAX_TOTAL) { toast.error("Project exceeds the 10-minute limit."); return; }
    setExporting(true);
    const spec = {
      clips: clips.map((c) => ({ url: c.url, trim: { start: +c.trimStart.toFixed(3), end: +c.trimEnd.toFixed(3) }, speed: +c.speed.toFixed(3) })),
      crop: crop ? { x: +crop.x.toFixed(4), y: +crop.y.toFixed(4), w: +crop.w.toFixed(4), h: +crop.h.toFixed(4) } : null,
      mute_original: !!audio?.muteOriginal,
      audio: audio ? { url: audio.url, volume: audio.volume } : null,
      overlays: [
        ...texts.map((x) => ({ type: "text", text: x.text, color: x.color, bold: x.bold, x: x.x, y: x.y, w: 0.6, h: x.h, start: +x.start.toFixed(2), end: +Math.min(x.end, total).toFixed(2) })),
        ...overlays.map((o) => ({ type: o.type, url: o.url, x: o.x, y: o.y, w: o.w, h: o.h, start: +(o.start || 0).toFixed(2), end: +Math.min(o.end ?? total, total).toFixed(2) })),
      ],
      origin_url: window.location.origin,
    };
    try { await api.post("/stitch/export", spec); toast.success("Exported to Creations 🎬"); onOpenChange(false); reset(); onExported && onExported(); }
    catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  const updEl = (id, patch, list) => (list === "text" ? setTexts : setOverlays)((a) => a.map((e) => e.id === id ? { ...e, ...patch } : e));
  const delEl = (id) => { setTexts((a) => a.filter((e) => e.id !== id)); setOverlays((a) => a.filter((e) => e.id !== id)); setSel(null); };
  const selText = texts.find((x) => x.id === sel);
  const selOverlay = overlays.find((x) => x.id === sel);

  if (!open) return null;
  const stageAspect = clips[0]?.w && clips[0]?.h ? `${clips[0].w}/${clips[0].h}` : "9/16";
  const showPicker = !clips.length || picking;

  return (
    <div className="fixed inset-0 z-[80] bg-ink-950 flex flex-col" data-testid="stitch-editor">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button onClick={() => { onOpenChange(false); reset(); }} data-testid="stitch-close" className="text-gold"><X size={24} /></button>
        <div className="flex items-center gap-2"><Scissors size={16} className="text-gold" /><span className="font-heading font-bold tracking-wide">Stitch</span>
          {clips.length > 0 && <span className="text-xs text-zinc-500 ml-2" data-testid="stitch-total">{fmt(total)} / 10:00</span>}</div>
        <div className="flex items-center gap-2">
        <button onClick={undo} disabled={!hist.past.length} data-testid="stitch-undo" title="Undo (Ctrl+Z)" aria-label="Undo"
          className="p-1.5 rounded-full text-gold hover:bg-[#1a140d] disabled:opacity-30 disabled:hover:bg-transparent transition-colors"><Undo2 size={18} /></button>
        <button onClick={redo} disabled={!hist.future.length} data-testid="stitch-redo" title="Redo (Ctrl+Shift+Z)" aria-label="Redo"
          className="p-1.5 rounded-full text-gold hover:bg-[#1a140d] disabled:opacity-30 disabled:hover:bg-transparent transition-colors"><Redo2 size={18} /></button>
        <button onClick={doExport} disabled={!clips.length || exporting} data-testid="stitch-export"
          className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold text-sm px-4 py-1.5 rounded-full disabled:opacity-50">
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Export</button>
        </div>
      </div>

      {showPicker ? (
        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="stitch-source-picker">
          <div className="text-center"><h2 className="font-heading text-xl font-bold">{clips.length ? "Add another clip" : "Start your edit"}</h2>
            <p className="text-sm text-zinc-500 mt-1">Pick a creation or upload a clip. Combine multiple clips (up to 10 min).</p></div>
          <button onClick={uploadVideo} data-testid="stitch-upload-source" className="w-full flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-xl py-3 hover:bg-[#1a140d]">
            {uploadPct != null ? <><Loader2 size={16} className="animate-spin" /> {uploadPct}%</> : <><Upload size={18} /> Upload videos</>}</button>
          <div className="grid grid-cols-3 gap-3">
            {myVideos.map((v) => (
              <button key={v.id} onClick={() => pickVideo(v.video_url)} data-testid={`stitch-pick-${v.id}`} className="aspect-[9/16] rounded-lg overflow-hidden border border-white/10 bg-ink-900 hover:border-gold">
                <video src={v.video_url} className="w-full h-full object-cover" muted preload="auto" playsInline /></button>))}
            {myVideos.length === 0 && <p className="col-span-3 text-center text-sm text-zinc-600 py-6">No video creations yet — upload one above.</p>}
          </div>
          {clips.length > 0 && <button onClick={() => setPicking(false)} className="w-full text-sm text-zinc-400 py-2">Back to editor</button>}
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 flex items-center justify-center bg-black relative p-2" onPointerDown={() => setSel(null)}>
            <div ref={stageRef} className="relative max-h-full" style={{ aspectRatio: stageAspect, height: "100%" }}>
              <video ref={videoRef} onTimeUpdate={onTimeUpdate} onError={() => toast.error("Couldn't load this clip in the browser.")}
                playsInline className="w-full h-full object-contain bg-black" data-testid="stitch-video" />
              {overlays.map((o) => ((rel >= o.start && rel <= o.end) || sel === o.id) && (
                <Draggable key={o.id} el={o} stageRef={stageRef} selected={sel === o.id} onSelect={() => setSel(o.id)} onChange={(p) => updEl(o.id, p, "overlay")}>
                  {o.type === "image" ? <img src={o.url} alt="" className="w-full h-full object-contain pointer-events-none" />
                    : <video src={o.url} className="w-full h-full object-contain pointer-events-none" muted autoPlay loop playsInline />}</Draggable>))}
              {texts.map((x) => ((rel >= x.start && rel <= x.end) || sel === x.id) && (
                <Draggable key={x.id} el={x} stageRef={stageRef} selected={sel === x.id} onSelect={() => setSel(x.id)} onChange={(p) => updEl(x.id, p, "text")}>
                  <span className="whitespace-pre pointer-events-none leading-tight" style={{ textShadow: "0 2px 6px rgba(0,0,0,.6)" }}>{x.text || " "}</span></Draggable>))}
              {tool === "crop" && crop && <CropBox crop={crop} setCrop={setCrop} stageRef={stageRef} />}
            </div>
          </div>

          <button onClick={togglePlay} data-testid="stitch-play" className="mx-auto my-2 w-11 h-11 rounded-full border border-gold/40 text-gold flex items-center justify-center">
            {playing ? <Pause size={20} /> : <Play size={20} />}</button>

          {/* filmstrip timeline */}
          <div className="px-3">
            <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-1"><span data-testid="stitch-time">{fmt(t)}</span><div className="flex-1" /><span>{fmt(total)}</span></div>
            <div className="relative">
              <input type="range" min={0} max={total || 0} step={0.05} value={t} onChange={(e) => seek(+e.target.value)} data-testid="stitch-scrubber" className="w-full accent-gold" />
              <div className="flex gap-1 mt-1 h-16" data-testid="stitch-filmstrip">
                {clips.map((c, i) => {
                  const w = total ? (eff(c) / total) * 100 : 100 / clips.length;
                  const isSel = selClip === c.id;
                  return (
                    <div key={c.id} data-testid={`stitch-clip-${c.id}`} data-clip-id={c.id} onClick={() => { setSelClip(c.id); seek(starts[i] + 0.01); }}
                      className={`relative rounded-md overflow-hidden border-2 cursor-pointer ${isSel ? "border-gold" : "border-transparent"} ${dragId === c.id ? "opacity-50 ring-2 ring-gold" : ""}`} style={{ width: `${w}%` }}>
                      {c.thumb
                        ? <img src={c.thumb} alt="" className="w-full h-full object-cover pointer-events-none" />
                        : <div className="w-full h-full bg-ink-900 flex items-center justify-center pointer-events-none"><Film size={16} className="text-zinc-600" /></div>}
                      <div className="absolute inset-0 bg-black/20" />
                      {clips.length > 1 && (
                        <button data-testid={`stitch-drag-${c.id}`} onClick={(e) => e.stopPropagation()} onPointerDown={onGripDown(c.id)}
                          className="absolute top-0.5 left-0.5 bg-black/60 rounded p-0.5 text-white/90 touch-none cursor-grab active:cursor-grabbing"><GripVertical size={12} /></button>
                      )}
                      <span className="absolute bottom-0.5 right-1 text-[9px] font-mono bg-black/60 px-1 rounded">{eff(c).toFixed(1)}s{c.speed !== 1 ? ` ${c.speed}x` : ""}</span>
                      {isSel && tool === "trim" && (<>
                        <span className="absolute left-0 inset-y-0 w-2 bg-gold" data-testid={`trim-l-${c.id}`} />
                        <span className="absolute right-0 inset-y-0 w-2 bg-gold" data-testid={`trim-r-${c.id}`} /></>)}
                    </div>);
                })}
                <button onClick={() => setPicking("add")} data-testid="stitch-add-clip" className="shrink-0 w-12 h-full rounded-md border border-dashed border-gold/50 text-gold flex items-center justify-center hover:bg-[#1a140d]"><Plus size={20} /></button>
              </div>
            </div>
          </div>

          <ToolPanel {...{ tool, cur, updClip, selClip, clips, splitAt, copyClip, delClip, moveClip, addText, selText, selOverlay, updEl, delEl, pickOverlay, pickAudio, capture, audio, setAudio, crop, setCrop, total, uploadPct }} />

          <div className="flex items-center gap-1 overflow-x-auto border-t border-white/10 px-2 py-2" data-testid="stitch-toolbar">
            {TOOLS.map((tl) => (
              <button key={tl.id} data-testid={`stitch-tool-${tl.id}`}
                onClick={() => { setTool(tl.id); if (tl.id === "capture") capture(); if (tl.id === "download") downloadClip(); if (tl.id === "split") splitAt(); if (tl.id === "copy") copyClip(); if (tl.id === "text") addText(); if (tl.id === "crop" && !crop) setCrop({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 }); }}
                className={`flex-1 min-w-[58px] flex flex-col items-center gap-1 py-1.5 rounded-lg ${tool === tl.id ? "text-gold" : "text-zinc-400 hover:text-zinc-200"}`}>
                <tl.icon size={19} /><span className="text-[10px] font-heading">{tl.label}</span></button>))}
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => <label className="text-xs text-zinc-400 flex flex-col gap-1">{label}{children}</label>;

function TimeRange({ el, onChange, total }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={`From (s)`}><input type="number" min={0} max={total} step={0.05} value={el.start} data-testid="stitch-el-start"
        onChange={(e) => onChange({ start: clamp(+e.target.value, 0, (el.end ?? total) - 0.05) })} className="bg-ink-900 border border-zinc-800 rounded px-2 py-1 text-sm" /></Field>
      <Field label={`Until (s)`}><input type="number" min={0} max={total} step={0.05} value={el.end ?? total} data-testid="stitch-el-end"
        onChange={(e) => onChange({ end: clamp(+e.target.value, (el.start || 0) + 0.05, total) })} className="bg-ink-900 border border-zinc-800 rounded px-2 py-1 text-sm" /></Field>
    </div>
  );
}

function ToolPanel({ tool, cur, updClip, selClip, clips, delClip, moveClip, selText, selOverlay, updEl, delEl, pickOverlay, pickAudio, audio, setAudio, crop, setCrop, total, uploadPct }) {
  if (tool === "trim" && cur) {
    return (
      <div className="px-4 py-3 border-t border-white/10 space-y-2" data-testid="stitch-trim-panel">
        <div className="flex items-center justify-between"><span className="text-xs text-zinc-400">Selected clip · {(cur.duration || 0).toFixed(1)}s source</span>
          <div className="flex items-center gap-2">
            <button onClick={() => moveClip(cur.id, -1)} disabled={clips[0]?.id === cur.id} className="text-zinc-300 disabled:opacity-30"><ChevronLeft size={18} /></button>
            <button onClick={() => moveClip(cur.id, 1)} disabled={clips[clips.length - 1]?.id === cur.id} className="text-zinc-300 disabled:opacity-30"><ChevronRight size={18} /></button>
            <button onClick={() => delClip(cur.id)} disabled={clips.length <= 1} data-testid="stitch-clip-delete" className="text-red-400 disabled:opacity-30"><Trash2 size={16} /></button></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Start ${cur.trimStart.toFixed(1)}s`}><input type="range" min={0} max={cur.duration} step={0.05} value={cur.trimStart} data-testid="stitch-trim-start"
            onChange={(e) => updClip(cur.id, { trimStart: clamp(+e.target.value, 0, cur.trimEnd - 0.2) })} className="accent-gold" /></Field>
          <Field label={`End ${cur.trimEnd.toFixed(1)}s`}><input type="range" min={0} max={cur.duration} step={0.05} value={cur.trimEnd} data-testid="stitch-trim-end"
            onChange={(e) => updClip(cur.id, { trimEnd: clamp(+e.target.value, cur.trimStart + 0.2, cur.duration) })} className="accent-gold" /></Field>
        </div>
      </div>);
  }
  if (tool === "speed" && cur) {
    return (
      <div className="px-4 py-3 border-t border-white/10 space-y-2" data-testid="stitch-speed-panel">
        <div className="flex items-center justify-between text-sm"><span className="text-zinc-300">Clip speed</span><span className="font-mono text-gold">{cur.speed.toFixed(2)}×</span></div>
        <input type="range" min={0.1} max={2} step={0.05} value={cur.speed} data-testid="stitch-speed" onChange={(e) => updClip(cur.id, { speed: +e.target.value })} className="w-full accent-gold" />
        <div className="flex justify-between text-[10px] text-zinc-500"><span>0.1× slow</span><span>1× normal</span><span>2× fast</span></div>
        <p className="text-xs text-zinc-500">Resulting length: <span className="text-zinc-300 font-mono">{eff(cur).toFixed(1)}s</span></p>
      </div>);
  }
  if (tool === "text" && selText) {
    return (
      <div className="px-4 py-3 border-t border-white/10 space-y-2" data-testid="stitch-text-panel">
        <div className="flex items-center gap-2">
          <input value={selText.text} onChange={(e) => updEl(selText.id, { text: e.target.value }, "text")} data-testid="stitch-text-input" className="flex-1 bg-ink-900 border border-zinc-800 rounded px-2 py-1.5 text-sm" placeholder="Type text…" />
          <input type="color" value={selText.color} onChange={(e) => updEl(selText.id, { color: e.target.value }, "text")} data-testid="stitch-text-color" className="w-9 h-9 rounded bg-transparent" />
          <button onClick={() => updEl(selText.id, { bold: !selText.bold }, "text")} className={`w-9 h-9 rounded border ${selText.bold ? "bg-gold text-ink-950 border-gold" : "border-zinc-700 text-zinc-300"}`}><Bold size={16} className="mx-auto" /></button>
          <button onClick={() => delEl(selText.id)} data-testid="stitch-text-delete" className="w-9 h-9 rounded border border-red-500/40 text-red-400"><Trash2 size={16} className="mx-auto" /></button>
        </div>
        <Field label={`Size ${(selText.h * 100).toFixed(0)}`}><input type="range" min={0.03} max={0.5} step={0.005} value={selText.h} onChange={(e) => updEl(selText.id, { h: +e.target.value }, "text")} data-testid="stitch-text-size" className="accent-gold" /></Field>
        <TimeRange el={selText} onChange={(p) => updEl(selText.id, p, "text")} total={total} />
      </div>);
  }
  if (tool === "overlay") {
    return (
      <div className="px-4 py-3 border-t border-white/10 space-y-2" data-testid="stitch-overlay-panel">
        {!selOverlay ? (
          <div className="flex gap-3">
            <button onClick={() => pickOverlay("image")} data-testid="stitch-add-image" className="flex-1 flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-lg py-2 text-sm">{uploadPct != null ? `${uploadPct}%` : <><Plus size={16} /> Image</>}</button>
            <button onClick={() => pickOverlay("video")} data-testid="stitch-add-video" className="flex-1 flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-lg py-2 text-sm"><Plus size={16} /> Video</button>
          </div>
        ) : (<>
          <div className="flex items-center justify-between"><span className="text-xs text-zinc-400 capitalize">{selOverlay.type} overlay</span>
            <button onClick={() => delEl(selOverlay.id)} className="text-red-400 flex items-center gap-1 text-xs"><Trash2 size={14} /> Remove</button></div>
          <Field label={`Size ${(selOverlay.w * 100).toFixed(0)}%`}><input type="range" min={0.05} max={1} step={0.01} value={selOverlay.w} onChange={(e) => updEl(selOverlay.id, { w: +e.target.value, h: +e.target.value }, "overlay")} className="accent-gold" /></Field>
          <TimeRange el={selOverlay} onChange={(p) => updEl(selOverlay.id, p, "overlay")} total={total} />
        </>)}
      </div>);
  }
  if (tool === "sound") {
    return (
      <div className="px-4 py-3 border-t border-white/10 space-y-3" data-testid="stitch-sound-panel">
        {!audio ? (
          <button onClick={pickAudio} data-testid="stitch-add-audio" className="w-full flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-lg py-2.5 text-sm">{uploadPct != null ? `${uploadPct}%` : <><Music size={16} /> Add music / audio</>}</button>
        ) : (<>
          <div className="flex items-center justify-between text-sm"><span className="truncate text-zinc-300 flex items-center gap-2"><Music size={15} className="text-gold" /> {audio.name}</span><button onClick={() => setAudio(null)} className="text-red-400"><Trash2 size={15} /></button></div>
          <Field label={`Volume ${(audio.volume * 100).toFixed(0)}%`}><input type="range" min={0} max={2} step={0.05} value={audio.volume} onChange={(e) => setAudio({ ...audio, volume: +e.target.value })} data-testid="stitch-volume" className="accent-gold" /></Field>
          <button onClick={() => setAudio({ ...audio, muteOriginal: !audio.muteOriginal })} data-testid="stitch-mute-original" className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border ${audio.muteOriginal ? "bg-gold text-ink-950 border-gold" : "border-zinc-700 text-zinc-300"}`}>
            {audio.muteOriginal ? <VolumeX size={16} /> : <Volume2 size={16} />} {audio.muteOriginal ? "Original audio muted" : "Keep original audio"}</button>
        </>)}
      </div>);
  }
  if (tool === "crop") {
    return (
      <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between" data-testid="stitch-crop-panel">
        <span className="text-xs text-zinc-400">Drag the box to crop the exported frame.</span>
        <button onClick={() => setCrop(null)} className="text-xs text-red-400 flex items-center gap-1"><Trash2 size={14} /> Clear crop</button></div>);
  }
  return null;
}
